/*
 * Berkeley Life events quiz.
 *
 * One card, one form, no page reloads. Answers are captured as each quiz step
 * completes, then contact details are submitted from the final step.
 *
 * Klaviyo is the backend. Events go through the client API instead of the
 * onsite queue: the theme's klaviyo_key setting is empty, so window.klaviyo is
 * not guaranteed to exist, and the client API also lets us send typed scalars.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'bl_event_quiz';
  var STATE_VERSION = 1;
  // Fallback only: the flow section is the source of truth for the wait.
  var RETEST_SECONDS = 5400;
  /* 2024-10-15 rejects the `subscriptions` object on the profile inside a
     client subscription with a 400, which silently dropped every list
     signup. 2025-01-15 is the earliest revision that accepts it. */
  var KLAVIYO_REVISION = '2025-01-15';
  var REQUEST_TIMEOUT = 8000;

  var BANDS = ['depleted', 'low', 'threshold', 'adequate', 'good', 'optimal'];

  var STEP_ERRORS = {
    step_reading: 'Select the band that matches your strip.',
    step_about: 'Answer both questions.',
    step_priorities: 'Pick at least one option.',
    step_practitioner: 'Choose one option.',
    step_capture: 'Check the highlighted field and try again.'
  };

  function readStored() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      // Private mode and locked-down devices throw on access. Not fatal: the
      // resume URL carries enough state to rebuild a session.
      return null;
    }
  }

  function writeStored(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {
      // Nothing downstream depends on storage succeeding.
    }
  }

  function newSessionId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'bl-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* E.164 or nothing. A malformed number would 400 the whole request and lose
     the lead, so an unparseable value is dropped and kept as a raw property. */
  function normalizePhone(raw) {
    if (!raw) return null;
    var trimmed = String(raw).trim();
    if (!trimmed) return null;
    var digits = trimmed.replace(/\D/g, '');
    if (trimmed.charAt(0) === '+') {
      return digits.length >= 8 && digits.length <= 15 ? '+' + digits : null;
    }
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    return null;
  }

  function csvToList(value) {
    if (!value) return [];
    return String(value)
      .split(',')
      .map(function (item) { return item.trim(); })
      .filter(Boolean);
  }

  function postWithRetry(url, body) {
    return postJson(url, body).catch(function (error) {
      return new Promise(function (resolve) { setTimeout(resolve, 800); })
        .then(function () { return postJson(url, body); })
        .catch(function () { throw error; });
    });
  }

  function postJson(url, body) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT) : null;

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body),
      keepalive: true,
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (timer) clearTimeout(timer);
      if (!response.ok) throw new Error('Klaviyo responded ' + response.status);
      return response;
    }).catch(function (error) {
      if (timer) clearTimeout(timer);
      throw error;
    });
  }

  customElements.define('bl-event-quiz', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = this.querySelector('[data-quiz-form]');
      if (!this.form) return;

      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-step]'));
      if (!this.steps.length) return;

      this.ready = true;
      this.total = this.steps.length;
      this.index = 0;
      this.progressLabel = this.querySelector('[data-progress-label]');
      this.progressStatus = this.querySelector('[data-progress-status]');
      this.conditional = this.querySelector('[data-conditional]');
      this.state = this.hydrate();

      this.steps.forEach(function (step) { step.setAttribute('tabindex', '-1'); });

      this.onClick = this.handleClick.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onInput = this.handleInput.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.form.addEventListener('submit', this.onSubmit);

      // Hidden steps are invisible in the theme editor, so selecting a step
      // block has to jump the card to that step.
      if (this.dataset.designMode) {
        this.onBlockSelect = this.handleBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.syncConditional();
      this.syncOptionCaps();
      this.syncSmsConsent();
      this.render();
      this.retryPendingSync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.ready = false;
    }

    /* ---------- state ---------- */

    hydrate() {
      var saved = readStored();
      var fresh = {
        v: STATE_VERSION,
        sessionId: newSessionId(),
        reading1: null,
        reading1Index: null,
        reading1At: null,
        ageRange: null,
        sex: null,
        menopauseStage: null,
        priorities: [],
        practitioner: null,
        firstName: null,
        email: null,
        phone: null,
        phoneRaw: null,
        isHcp: false,
        smsConsent: false,
        dryRun: false,
        retestDueAt: null,
        reading2: null,
        reading2Index: null,
        reading2At: null,
        synced: false
      };

      // Reuse an unfinished session from the same device so a refresh mid-flow
      // does not create a second sessionId for the same visitor. A completed
      // session is never reused: the next person at the booth is a new lead.
      if (saved && saved.v === STATE_VERSION && !saved.email && typeof saved.sessionId === 'string') {
        var age = Math.floor(Date.now() / 1000) - (saved.reading1At || 0);
        if (!saved.reading1At || (age >= 0 && age < 86400)) {
          fresh.sessionId = saved.sessionId;
          if (saved.reading1At) fresh.reading1At = saved.reading1At;
        }
      }
      return fresh;
    }

    /* The flow section owns the wait, and retest_due_at is what the Klaviyo SMS
       is scheduled against, so both have to read the same number. */
    retestDelay() {
      var configured = RETEST_SECONDS;
      var node = document.querySelector('[data-bl-wait]');
      if (node) {
        var parsed = parseInt(node.dataset.blWait, 10);
        if (parsed > 0) configured = parsed;
      }

      var override = new URLSearchParams(window.location.search).get('bl_test_delay');
      var seconds = parseInt(override, 10);
      // QA shortcut so nobody waits the full window per test run.
      if (override !== null && Number.isFinite(seconds) && seconds > 0 && seconds <= configured) {
        return seconds;
      }
      return configured;
    }

    /* ---------- navigation ---------- */

    currentStep() {
      return this.steps[this.index];
    }

    render() {
      var self = this;
      this.steps.forEach(function (step, position) {
        var active = position === self.index;
        step.hidden = !active;
        var fieldset = step.querySelector('fieldset');
        // Disabled controls are skipped by validation and by FormData, which
        // keeps inactive steps from blocking the active one.
        if (fieldset) fieldset.disabled = !active;
      });

      var human = this.index + 1;
      var percent = Math.round((human / this.total) * 100);
      this.style.setProperty('--sl-events-progress', percent + '%');
      if (this.progressLabel) this.progressLabel.textContent = human + '/' + this.total;
      if (this.progressStatus) this.progressStatus.textContent = 'Step ' + human + ' of ' + this.total;

      if (this.currentStep().dataset.step === 'step_capture') {
        this.renderRecap();
        this.syncSmsConsent();
      }
    }

    renderRecap() {
      var captureStep = this.form.querySelector('[data-step="step_capture"]');
      var recap = captureStep && captureStep.querySelector('[data-recap]');
      if (!recap || !this.state.reading1) return;

      var dot = recap.querySelector('[data-recap-dot]');
      var band = recap.querySelector('[data-recap-band]');
      var meta = recap.querySelector('[data-recap-meta]');
      var input = this.form.querySelector('[name="reading_1"][value="' + this.state.reading1 + '"]');
      var label = input ? input.parentElement.querySelector('.c-events-hero__option-label') : null;

      if (dot) dot.className = 'c-events-hero__recap-dot c-events-hero__swatch--' + this.state.reading1;
      if (band) band.textContent = label ? label.textContent.trim() : this.state.reading1;
      if (meta) {
        var at = this.state.reading1At ? new Date(this.state.reading1At * 1000) : new Date();
        var time = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        meta.textContent = (meta.dataset.label || 'Reading 1') + ' · ' + time;
      }
      recap.hidden = false;
    }

    goTo(position) {
      if (position < 0 || position >= this.total) return;
      this.index = position;
      this.render();

      var step = this.currentStep();
      step.focus({ preventScroll: true });

      // Only pull the card back into view when it has scrolled off the top,
      // so advancing never yanks the page on a phone.
      var box = this.getBoundingClientRect();
      if (box.top < 0 || box.top > window.innerHeight * 0.6) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    /* ---------- interaction ---------- */

    handleClick(event) {
      /* Going back never validates and never captures: the point is to let
         someone fix an answer they already regret. Values stay in the inputs,
         so the step is exactly as they left it. */
      if (event.target.closest('[data-step-back]')) {
        event.preventDefault();
        if (this.busy) return;
        this.goTo(this.index - 1);
        return;
      }

      var button = event.target.closest('[data-step-advance]');
      if (!button || button.type === 'submit') return;
      event.preventDefault();
      this.advance();
    }

    handleChange(event) {
      var target = event.target;
      if (!target.name) return;

      if (target.name === 'sex' || target.name === 'age_range') this.syncConditional();
      if (target.name === 'priorities') this.syncOptionCaps();

      this.clearError();
      if ((target.name === 'reading_1' || target.name === 'priorities') && target.checked) {
        var selectedStep = target.closest('[data-step]');
        var self = this;
        window.setTimeout(function () {
          if (self.currentStep() === selectedStep) self.advance();
        }, 180);
      }
    }

    handleInput(event) {
      if (event.target.name === 'phone') this.syncSmsConsent();
      this.clearError();
    }

    handleBlockSelect(event) {
      var blockId = event.detail && event.detail.blockId;
      if (!blockId) return;
      for (var i = 0; i < this.steps.length; i += 1) {
        if (this.steps[i].dataset.blockId === blockId) {
          this.index = i;
          this.render();
          return;
        }
      }
    }

    /* The menopause question is only meaningful for part of the audience, and
       stale answers must not leak into Klaviyo, so hidden inputs are cleared
       and disabled on every pass, including the first. */
    syncConditional() {
      if (!this.conditional) return;

      var sexes = csvToList(this.conditional.dataset.whenSex);
      var ages = csvToList(this.conditional.dataset.whenAge);
      var sex = this.fieldValue('sex');
      var age = this.fieldValue('age_range');
      var show = (!sexes.length || sexes.indexOf(sex) !== -1) && (!ages.length || ages.indexOf(age) !== -1);

      this.conditional.hidden = !show;
      Array.prototype.forEach.call(this.conditional.querySelectorAll('input'), function (input) {
        if (!show && input.checked) input.checked = false;
        input.disabled = !show;
      });
    }

    /* Enforced in the UI, not only on submit: past the cap the rest lock. */
    syncOptionCaps() {
      var group = this.querySelector('[data-option-max]');
      if (!group) return;

      var max = parseInt(group.dataset.optionMax, 10);
      if (!Number.isFinite(max) || max < 1) return;

      var inputs = Array.prototype.slice.call(group.querySelectorAll('input'));
      var checked = inputs.filter(function (input) { return input.checked; }).length;
      inputs.forEach(function (input) {
        input.disabled = !input.checked && checked >= max;
      });
    }

    syncSmsConsent() {
      var consent = this.querySelector('[data-sms-consent]');
      if (!consent) return;
      var phone = this.form.querySelector('[name="phone"]');
      var box = consent.querySelector('input');
      var hasPhone = Boolean(phone && normalizePhone(phone.value));
      // Consent without a usable number is meaningless and would be a false
      // positive in every SMS segment built on it.
      if (!hasPhone) box.checked = false;
      box.disabled = !hasPhone;
      consent.style.opacity = hasPhone ? '' : '0.55';
    }

    /* ---------- validation ---------- */

    fieldValue(name) {
      var nodes = this.form.querySelectorAll('[name="' + name + '"]');
      if (!nodes.length) return null;
      var first = nodes[0];
      if (first.type === 'radio' || first.type === 'checkbox') {
        var picked = this.form.querySelector('[name="' + name + '"]:checked');
        return picked ? picked.value : null;
      }
      return first.value ? first.value.trim() : null;
    }

    checkedValues(name) {
      return Array.prototype.slice
        .call(this.form.querySelectorAll('[name="' + name + '"]:checked'))
        .map(function (input) { return input.value; });
    }

    showError(message, step) {
      var slot = (step || this.currentStep()).querySelector('[data-step-error]');
      if (!slot) return;
      slot.textContent = message;
      slot.hidden = false;
    }

    clearError(step) {
      var slot = (step || this.currentStep()).querySelector('[data-step-error]');
      if (slot) slot.hidden = true;
    }

    validateStep() {
      var step = this.currentStep();
      var type = step.dataset.step;

      if (type === 'step_priorities') {
        if (!this.checkedValues('priorities').length) {
          this.showError(STEP_ERRORS.step_priorities);
          return false;
        }
        this.clearError();
        return true;
      }

      var controls = Array.prototype.slice.call(step.querySelectorAll('input, select, textarea'));
      for (var i = 0; i < controls.length; i += 1) {
        var control = controls[i];
        if (control.disabled || control.checkValidity()) continue;
        var grouped = control.type === 'radio' || control.type === 'checkbox';
        // "Please enter an email address" beats a generic step message, but a
        // radio group reads better with the wording written for that step.
        this.showError(grouped
          ? (STEP_ERRORS[type] || control.validationMessage)
          : (control.validationMessage || STEP_ERRORS[type] || 'Check this field.'));
        if (!grouped) control.focus();
        return false;
      }
      this.clearError();
      return true;
    }

    /* ---------- step handlers ---------- */

    advance() {
      if (!this.validateStep()) return;
      this.captureStep();
      writeStored(this.state);
      this.goTo(this.index + 1);
    }

    captureStep(step) {
      var sourceStep = step || this.currentStep();
      if (!sourceStep) return;
      var type = sourceStep.dataset.step;

      if (type === 'step_reading') {
        var band = this.fieldValue('reading_1');
        this.state.reading1 = band;
        this.state.reading1Index = BANDS.indexOf(band);
        if (!this.state.reading1At) this.state.reading1At = Math.floor(Date.now() / 1000);
        this.state.retestDueAt = this.state.reading1At + this.retestDelay();
        this.trackQuizStarted();
      }

      if (type === 'step_about') {
        this.state.ageRange = this.fieldValue('age_range');
        this.state.sex = this.fieldValue('sex');
        this.state.menopauseStage = this.conditional && !this.conditional.hidden
          ? this.fieldValue('menopause_stage')
          : null;
      }

      if (type === 'step_priorities') this.state.priorities = this.checkedValues('priorities');
      if (type === 'step_practitioner') this.state.practitioner = this.fieldValue('practitioner_status');

      if (type === 'step_capture') {
        var phoneField = this.form.querySelector('[name="phone"]');
        this.state.firstName = this.fieldValue('first_name');
        this.state.email = this.fieldValue('email');
        this.state.phone = normalizePhone(phoneField ? phoneField.value : null);
        this.state.phoneRaw = phoneField && phoneField.value.trim() ? phoneField.value.trim() : null;
        this.state.isHcp = Boolean(this.form.querySelector('[name="is_hcp"]:checked'));
        this.state.smsConsent = Boolean(this.form.querySelector('[name="sms_consent"]:checked')) && Boolean(this.state.phone);
      }
    }

    handleSubmit(event) {
      event.preventDefault();
      if (this.busy) return;
      if (!this.validateStep()) return;
      this.captureStep();

      writeStored(this.state);

      var button = this.currentStep().querySelector('[data-step-advance]');
      var original = button ? button.textContent : '';
      this.busy = true;
      if (button) {
        button.disabled = true;
        button.textContent = 'Sending…';
      }

      var self = this;
      var state = this.state;
      this.submitReading(state)
        .catch(function (error) {
          // The lead is already in localStorage and gets retried on next load,
          // so a failed send must never trap the visitor on this step.
          if (window.console) console.warn('[bl-event-quiz] Klaviyo send failed', error);
        })
        .then(function () {
          self.busy = false;
          if (button) {
            button.disabled = false;
            button.textContent = original;
          }
          self.finish(button, original);
        });
    }

    /* ---------- Klaviyo ---------- */

    isDryRun() {
      return this.dataset.dryRun === 'true';
    }

    klaviyoUrl(resource) {
      var account = this.dataset.klaviyoAccount;
      if (!account) return null;
      return 'https://a.klaviyo.com/client/' + resource + '/?company_id=' + encodeURIComponent(account);
    }

    eventProperties(state) {
      var props = {
        reading_1: state.reading1,
        reading_1_index: state.reading1Index,
        age_range: state.ageRange,
        sex: state.sex,
        menopause_stage: state.menopauseStage,
        // Scalars, never an array: a previous integration sent ["120"] and
        // every numeric segment built on it failed silently.
        priority_1: state.priorities[0] || null,
        priority_2: state.priorities[1] || null,
        practitioner_status: state.practitioner,
        is_hcp: state.isHcp,
        sms_consent: state.smsConsent,
        session_id: state.sessionId,
        source: this.dataset.source || null,
        event_name: this.dataset.eventName || null
      };
      if (state.retestDueAt) props.retest_due_at = new Date(state.retestDueAt * 1000).toISOString();
      var resume = this.resumeUrl(state);
      if (resume) props.resume_url = resume;
      return props;
    }

    profileProperties(state) {
      return {
        no_reading_1: state.reading1,
        no_reading_1_index: state.reading1Index,
        no_is_hcp: state.isHcp,
        no_menopause_stage: state.menopauseStage,
        no_practitioner_status: state.practitioner,
        no_session_id: state.sessionId,
        no_summit_2026: true
      };
    }

    /* One setting drives both halves: a practitioner being handed to another
       destination should not also be dropped into a nurture list. Only active
       with a destination set, because skipping the list without redirecting
       would lose the lead and leave them on the consumer result screen. */
    hcpRedirectUrl(state) {
      if (!state || !state.isHcp) return null;
      var target = this.dataset.hcpRedirectUrl;
      if (!target) return null;
      try {
        var url = new URL(target, window.location.href);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
        return url.href;
      } catch (_) {
        return null;
      }
    }

    resumeUrl(state) {
      var base = this.dataset.retestUrl;
      if (!base || !state.reading1 || !state.reading1At) return null;
      try {
        var url = new URL(base, window.location.href);
        url.searchParams.set('sid', state.sessionId);
        url.searchParams.set('r1', state.reading1);
        url.searchParams.set('t', String(state.reading1At));
        return url.href;
      } catch (_) {
        return null;
      }
    }

    profileAttributes(state) {
      var attributes = { email: state.email, first_name: state.firstName };
      if (state.phone) attributes.phone_number = state.phone;
      return attributes;
    }

    trackQuizStarted() {
      if (this.dataset.trackStarted !== 'true' || this.startedSent) return;
      var url = this.klaviyoUrl('events');
      if (!url) return;
      this.startedSent = true;

      if (this.isDryRun()) {
        if (window.console) console.warn('[bl-event-quiz] Dry run, NO Quiz Started not sent.');
        return;
      }

      // Anonymous on purpose: this metric only measures drop-off before capture.
      postJson(url, {
        data: {
          type: 'event',
          attributes: {
            properties: {
              session_id: this.state.sessionId,
              reading_1: this.state.reading1,
              source: this.dataset.source || null,
              event_name: this.dataset.eventName || null
            },
            unique_id: this.state.sessionId + '-started',
            metric: { data: { type: 'metric', attributes: { name: 'NO Quiz Started' } } },
            profile: { data: { type: 'profile', attributes: { anonymous_id: this.state.sessionId } } }
          }
        }
      }).catch(function () {
        // Funnel telemetry only. Never surfaced to the visitor.
      });
    }

    submitReading(state) {
      var self = this;
      var profilesUrl = this.klaviyoUrl('profiles');
      var eventsUrl = this.klaviyoUrl('events');
      if (!profilesUrl || !eventsUrl) return Promise.reject(new Error('Klaviyo account id missing'));
      if (!state.email) return Promise.reject(new Error('No email to identify'));

      if (this.isDryRun()) {
        state.dryRun = true;
        if (window.console) {
          console.warn(
            '[bl-event-quiz] Dry run. Nothing was sent to Klaviyo. Payloads that would have gone out:',
            {
              profile: this.profileAttributes(state),
              profile_properties: this.profileProperties(state),
              list_id: state.isHcp ? this.dataset.listHcp : this.dataset.listDtc,
              event: this.eventProperties(state)
            }
          );
        }
        // Held briefly so the submit animation runs the same as in production.
        var self = this;
        return new Promise(function (resolve) { setTimeout(resolve, 600); })
          .then(function () { self.markSynced(state); });
      }

      // Klaviyo drops events for profiles it cannot resolve, without an error,
      // so the profile is written and awaited before the event is sent.
      var identify = {
        data: {
          type: 'profile',
          attributes: this.profileAttributes(state)
        }
      };
      identify.data.attributes.properties = this.profileProperties(state);

      return postWithRetry(profilesUrl, identify)
        .then(function () {
          return Promise.all([self.subscribe(state), self.trackReading(eventsUrl, state)]);
        })
        .then(function () {
          self.markSynced(state);
          self.pushOnsiteIdentify(state);
        });
    }

    trackReading(eventsUrl, state) {
      return postWithRetry(eventsUrl, {
        data: {
          type: 'event',
          attributes: {
            properties: this.eventProperties(state),
            unique_id: state.sessionId + '-reading-1',
            time: new Date((state.reading1At || Math.floor(Date.now() / 1000)) * 1000).toISOString(),
            metric: { data: { type: 'metric', attributes: { name: 'NO Reading 1 Submitted' } } },
            profile: { data: { type: 'profile', attributes: this.profileAttributes(state) } }
          }
        }
      });
    }

    subscribe(state) {
      // Being sent elsewhere is the follow-up, so a list signup would be the
      // wrong one. The profile and the reading event still go out.
      if (this.hcpRedirectUrl(state)) return Promise.resolve();

      var listId = state.isHcp ? this.dataset.listHcp : this.dataset.listDtc;
      var url = this.klaviyoUrl('subscriptions');
      if (!listId || !url) return Promise.resolve();

      var subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
      // SMS consent is only ever sent alongside a number Klaviyo will accept.
      if (state.smsConsent && state.phone) {
        subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
      }

      var attributes = { email: state.email, subscriptions: subscriptions };
      if (state.phone) attributes.phone_number = state.phone;

      return postJson(url, {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.dataset.eventName || 'Events LP',
            profile: { data: { type: 'profile', attributes: attributes } }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      }).catch(function (error) {
        // Consent can fail on an already-suppressed profile. The reading event
        // still needs to land, so this failure is swallowed deliberately.
        if (window.console) console.warn('[bl-event-quiz] subscription skipped', error);
      });
    }

    /* Links the browser cookie to the profile when the onsite script is present,
       so later onsite behaviour attaches to the same person. */
    pushOnsiteIdentify(state) {
      if (this.isDryRun()) return;
      try {
        var payload = { $email: state.email, $first_name: state.firstName };
        if (state.phone) payload.$phone_number = state.phone;
        window._learnq = window._learnq || [];
        window._learnq.push(['identify', payload]);
      } catch (_) {
        // Onsite is optional.
      }
    }

    /* Storage is shared by everyone who uses the booth device, so a late retry
       must never stamp its result over a session that has since started. */
    markSynced(state) {
      state.synced = true;
      var current = readStored();
      if (current && current.sessionId !== state.sessionId) return;
      writeStored(state);
    }

    retryPendingSync() {
      var saved = readStored();
      if (!saved || saved.v !== STATE_VERSION || !saved.email || saved.synced || saved.dryRun) return;
      // A booth network drops requests. Resend once on the next page load,
      // against a copy so the live session keeps its own state.
      this.submitReading(saved).catch(function () {});
    }

    /* ---------- completion ---------- */

    finish(button, originalLabel) {
      var detail = { state: this.state, resumeUrl: this.resumeUrl(this.state) };
      document.body.classList.add('bl-quiz-submitted');
      // Visibility belongs to the flow section, which listens for this event.
      document.dispatchEvent(new CustomEvent('bl:event-quiz:submitted', { detail: detail, bubbles: true }));

      // Checked first: a practitioner never continues into the consumer
      // result and retest journey.
      var hcpTarget = this.hcpRedirectUrl(this.state);
      if (hcpTarget && !this.dataset.designMode) {
        window.location.assign(hcpTarget);
        return;
      }

      var target = this.dataset.resultUrl;
      if (target && !this.dataset.designMode) {
        var url = this.resultUrl(target);
        if (url) {
          window.location.assign(url);
          return;
        }
      }
      // No result page yet: the card stays put and the sections listening for
      // bl:event-quiz:submitted take over. The button still has to confirm
      // something happened, otherwise the submit reads as a dead tap.
      this.setAttribute('data-submitted', 'true');
      if (button) {
        button.disabled = true;
        button.textContent = this.dataset.submittedLabel || originalLabel;
      }
    }

    resultUrl(target) {
      try {
        var url = new URL(target, window.location.href);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
        url.searchParams.set('sid', this.state.sessionId);
        if (this.state.reading1) url.searchParams.set('r1', this.state.reading1);
        if (this.state.reading1At) url.searchParams.set('t', String(this.state.reading1At));
        return url.href;
      } catch (_) {
        return null;
      }
    }

  });
})();
